import { type FetchResult, useMutation } from '@apollo/client';
import { type FormikHelpers, useFormik } from 'formik';
import type React from 'react';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import createFieldDefMutation from 'src/api/admin/createFieldDefMutation';
import { useCustomToast } from 'src/hooks/menu';
import * as Yup from 'yup';
import { isDateFieldType, isMuliValueFieldType, resolveFieldType } from '../../helpers';
import type { MetadataFieldFormValues, ScopeFilter } from '../../types';
import AdminFormModal from './AdminFormModal';
import MetadataFieldForm from './MetadataFieldForm';

interface CreateMetadataFieldModalProps {
  onClose: (value: boolean) => void;
  defaultScopeFilter?: ScopeFilter;
}

interface CreateFieldDefResponse {
  createFieldDef: {
    fieldDef: {
      id: string;
      name: string;
    };
  };
}

const CreateMetadataFieldModal: React.FC<CreateMetadataFieldModalProps> = ({ onClose, defaultScopeFilter = 'all' }) => {
  const { toastr } = useCustomToast();
  const [createFieldDef] = useMutation<CreateFieldDefResponse>(createFieldDefMutation, {
    fetchPolicy: 'network-only',
    refetchQueries: [allFieldDefinitionsQuery],
  });

  const onSuccess = (
    name: string,
    values: MetadataFieldFormValues,
    submitProps: FormikHelpers<MetadataFieldFormValues>,
  ): void => {
    toastr.success(`Field '${name}' was created successfully`);
    //reseting the form so formik.dirty is set to false after submitting the form and so the unsaved prompt warning won't show up after saving and leaving the route
    submitProps.resetForm({ values });
    onClose(false);
  };

  const onSubmit = async (
    values: MetadataFieldFormValues,
    submitProps: FormikHelpers<MetadataFieldFormValues>,
  ): Promise<void> => {
    const {
      name,
      fieldType,
      datePrecision,
      dateDisplayStyle,
      isEditable,
      highlightIfBlank,
      choices,
      scopeAssets,
      scopeGalleries,
    } = values;

    // Resolve 'DATE' sentinel to the actual backend type code
    const resolvedFieldType = resolveFieldType(fieldType, datePrecision);

    const scope: string[] = [];
    if (scopeAssets) scope.push('asset');
    if (scopeGalleries) scope.push('collection.gallery');

    if (scope.length === 0) {
      toastr.error('Please select at least one Applies To option');
      return;
    }

    const handleSuccess = (): void => {
      onSuccess(name, values, submitProps);
    };

    const handleError = (e: Error): void => {
      toastr.error(e.message);
    };

    const createMutation = (): Promise<FetchResult<CreateFieldDefResponse>> => {
      if (isMuliValueFieldType(resolvedFieldType)) {
        return createFieldDef({
          variables: {
            name,
            fieldType: resolvedFieldType,
            choices,
            highlightIfBlank,
            isEditable,
            dateDisplayStyle:
              isDateFieldType(resolvedFieldType) && resolvedFieldType !== 'YO' ? dateDisplayStyle : undefined,
            scope,
          },
        });
      } else {
        return createFieldDef({
          variables: {
            name,
            fieldType: resolvedFieldType,
            highlightIfBlank,
            isEditable,
            dateDisplayStyle:
              isDateFieldType(resolvedFieldType) && resolvedFieldType !== 'YO' ? dateDisplayStyle : undefined,
            scope,
          },
        });
      }
    };

    createMutation().then(handleSuccess).catch(handleError);
  };

  const formik = useFormik<MetadataFieldFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: '',
      fieldType: 'TT',
      datePrecision: 'DA',
      dateDisplayStyle: 'iso',
      highlightIfBlank: false,
      isEditable: false,
      choices: [],
      scopeAssets: defaultScopeFilter !== 'gallery',
      scopeGalleries: defaultScopeFilter === 'gallery',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please provide a field name'),
    }),
    onSubmit: async (values, submitProps) => {
      onSubmit(values, submitProps);
    },
  });

  return (
    <AdminFormModal header="Create Field Definition" formik={formik} onClose={() => onClose(false)}>
      <MetadataFieldForm formik={formik} />
    </AdminFormModal>
  );
};

export default CreateMetadataFieldModal;
